import { useMemo, useState } from "react";
import Card, { SectionTitle } from "@/components/base/Card";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import Modal from "@/components/base/Modal";
import Tabs from "@/components/base/Tabs";
import { Input, Textarea } from "@/components/base/Field";
import { LoadingState, ErrorState, EmptyState } from "@/components/base/StateViews";
import { useToast } from "@/components/base/Toast";
import { useAsync } from "@/hooks/useAsync";
import { usePager } from "@/hooks/usePager";
import Pagination from "@/components/base/Pagination";
import {
  approveAccountRequest,
  listAccountRequests,
  rejectAccountRequest,
} from "@/services/requestService";
import type { CreatedAdmin } from "@/services/platformService";
import { CYCLE_SCOPE_LABEL } from "@/utils/cycleScope";
import { formatDate } from "@/utils/format";
import TemporaryPasswordDialog from "@/components/feature/TemporaryPasswordDialog";
import type { AccountRequest, SchoolPlan } from "@/types";
import PlanPicker from "@/components/feature/PlanPicker";
import { PLAN_LABEL, PLAN_MAX_CYCLES } from "@/utils/plans";

interface AccountRequestsPanelProps {
  /** Appelé après une validation pour rafraîchir la liste des écoles. */
  onChanged: () => void | Promise<void>;
}

const statusTone: Record<AccountRequest["status"], "accent" | "primary" | "neutral"> = {
  PENDING: "accent",
  APPROVED: "primary",
  REJECTED: "neutral",
};

const statusLabel: Record<AccountRequest["status"], string> = {
  PENDING: "En attente",
  APPROVED: "Validée",
  REJECTED: "Refusée",
};

const CODE_RE = /^[A-Z0-9-]{2,20}$/;

/**
 * Demandes déposées depuis la page publique. Valider crée l'école (cycles, année, rôles) et le compte
 * administrateur du demandeur, dont le mot de passe provisoire est affiché une seule fois.
 */
export default function AccountRequestsPanel({ onChanged }: AccountRequestsPanelProps) {
  const { notify } = useToast();
  const { data, loading, error, reload } = useAsync(() => listAccountRequests(), []);
  const [tab, setTab] = useState("attente");
  const [busyId, setBusyId] = useState<string | null>(null);
  /** Décision en cours de saisie (code de l'école ou motif du refus). */
  const [decision, setDecision] = useState<{ request: AccountRequest; kind: "approve" | "reject" } | null>(null);
  const [value, setValue] = useState("");
  const [plan, setPlan] = useState<SchoolPlan>("ETABLISSEMENT");
  const [endsAt, setEndsAt] = useState("");
  const [created, setCreated] = useState<{ admin: CreatedAdmin; schoolName: string } | null>(null);
  const pager = usePager();

  const requests = useMemo(() => data ?? [], [data]);

  const filtered = useMemo(() => {
    if (tab === "toutes") return requests;
    if (tab === "attente") return requests.filter((r) => r.status === "PENDING");
    return requests.filter((r) => r.status === tab);
  }, [requests, tab]);
  const page = pager.slice(filtered, tab);

  const pendingCount = requests.filter((r) => r.status === "PENDING").length;

  const openDecision = (request: AccountRequest, kind: "approve" | "reject") => {
    setValue(kind === "approve" ? request.code : "");
    setPlan(request.plan);
    setEndsAt("");
    setDecision({ request, kind });
  };

  const confirmDecision = async () => {
    if (!decision) return;
    const { request, kind } = decision;
    if (kind === "approve" && !CODE_RE.test(value.trim())) {
      notify("Code de 2 à 20 caractères : lettres, chiffres ou tirets.", "error");
      return;
    }
    const maxCycles = PLAN_MAX_CYCLES[plan];
    if (kind === "approve" && maxCycles !== null && request.cycles.length > maxCycles) {
      notify(`Le forfait ${PLAN_LABEL[plan]} comprend un seul cycle : cette école en demande ${request.cycles.length}.`, "error");
      return;
    }
    if (kind === "reject" && !value.trim()) {
      notify("Indiquez le motif du refus.", "error");
      return;
    }
    setBusyId(request.id);
    try {
      if (kind === "approve") {
        const res = await approveAccountRequest(request.id, value, { plan, subscriptionEndsAt: endsAt });
        setCreated({ admin: res.admin, schoolName: res.school.name });
        notify(`Demande validée : l'école « ${res.school.shortName} » est ouverte.`);
        await onChanged();
      } else {
        await rejectAccountRequest(request.id, value);
        notify(`Demande de « ${request.schoolName} » refusée.`, "info");
      }
      setDecision(null);
      await reload();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Opération impossible.", "error");
    } finally {
      setBusyId(null);
    }
  };

  const tabs = [
    { id: "attente", label: "En attente", icon: "ri-time-line", count: pendingCount },
    {
      id: "APPROVED",
      label: "Validées",
      icon: "ri-checkbox-circle-line",
      count: requests.filter((r) => r.status === "APPROVED").length,
    },
    {
      id: "REJECTED",
      label: "Refusées",
      icon: "ri-close-circle-line",
      count: requests.filter((r) => r.status === "REJECTED").length,
    },
    { id: "toutes", label: "Toutes", icon: "ri-list-check-2", count: requests.length },
  ];

  return (
    <Card className="mt-6">
      <SectionTitle
        title="Demandes d'ouverture de compte"
        subtitle="Seul l'administrateur de la plateforme valide la création des nouvelles écoles"
        icon="ri-mail-open-line"
        action={<Tabs tabs={tabs} active={tab} onChange={setTab} />}
      />

      {loading ? (
        <LoadingState label="Chargement des demandes..." />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="ri-inbox-line"
          title="Aucune demande dans cette catégorie"
          message="Les demandes déposées depuis la page publique apparaîtront ici pour être validées ou refusées."
        />
      ) : (
        <>
        <div className="space-y-3">
          {page.items.map((req) => (
            <div
              key={req.id}
              className="rounded-lg border border-background-200 bg-background-100/40 p-4"
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h4 className="text-sm font-semibold text-foreground-950">{req.schoolName}</h4>
                    <Badge tone={statusTone[req.status]}>{statusLabel[req.status]}</Badge>
                    <span className="text-xs text-foreground-400">{req.reference}</span>
                    {req.code && (
                      <span className="text-xs text-foreground-400">Code {req.code}</span>
                    )}
                  </div>

                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-map-pin-line text-foreground-400"></i>
                      {req.city}
                      {req.country ? `, ${req.country}` : ""}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-user-line text-foreground-400"></i>
                      {req.contactName}
                      {req.contactRole ? ` · ${req.contactRole}` : ""}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-mail-line text-foreground-400"></i>
                      {req.email}
                    </span>
                    {req.phone && (
                      <span className="flex items-center gap-1.5">
                        <i className="ri-phone-line text-foreground-400"></i>
                        {req.phone}
                      </span>
                    )}
                    <span className="flex items-center gap-1.5">
                      <i className="ri-calendar-line text-foreground-400"></i>
                      Déposée le {formatDate(req.createdAt)}
                    </span>
                  </div>

                  <div className="mt-2.5 flex flex-wrap items-center gap-2">
                    <Badge tone="primary" icon="ri-vip-crown-line">
                      {PLAN_LABEL[req.plan]}
                    </Badge>
                    {req.cycles.map((c) => (
                      <Badge key={c} tone="secondary" icon="ri-building-4-line">
                        {CYCLE_SCOPE_LABEL[c]}
                      </Badge>
                    ))}
                    {req.studentsEstimate && (
                      <Badge tone="neutral" icon="ri-group-line">
                        {req.studentsEstimate} élèves
                      </Badge>
                    )}
                  </div>

                  {req.message && (
                    <p className="mt-3 rounded-md border border-background-200 bg-background-50 px-3 py-2 text-xs leading-relaxed text-foreground-600">
                      {req.message}
                    </p>
                  )}

                  {req.status !== "PENDING" && req.reviewedBy && (
                    <p className="mt-2 text-xs text-foreground-400">
                      {statusLabel[req.status]} par {req.reviewedBy}
                      {req.reviewedAt ? ` le ${formatDate(req.reviewedAt)}` : ""}
                      {req.rejectionReason ? ` — motif : ${req.rejectionReason}` : ""}
                    </p>
                  )}
                </div>

                {req.status === "PENDING" && (
                  <div className="flex shrink-0 items-center gap-2 lg:flex-col lg:items-stretch">
                    <Button
                      size="sm"
                      icon="ri-check-line"
                      onClick={() => openDecision(req, "approve")}
                      disabled={busyId === req.id}
                    >
                      Valider
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      icon="ri-close-line"
                      onClick={() => openDecision(req, "reject")}
                      disabled={busyId === req.id}
                    >
                      Refuser
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <Pagination pager={page} className="mt-3 rounded-md border border-background-200 bg-background-50" />
        </>
      )}

      <Modal
        open={decision !== null}
        onClose={() => setDecision(null)}
        title={decision?.kind === "approve" ? "Valider la demande" : "Refuser la demande"}
        subtitle={decision?.request.schoolName}
        icon={decision?.kind === "approve" ? "ri-checkbox-circle-line" : "ri-close-circle-line"}
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setDecision(null)}>
              Annuler
            </Button>
            <Button
              variant={decision?.kind === "approve" ? "primary" : "danger"}
              icon={decision?.kind === "approve" ? "ri-check-line" : "ri-close-line"}
              onClick={confirmDecision}
              disabled={busyId !== null}
            >
              {busyId ? "Traitement..." : decision?.kind === "approve" ? "Ouvrir l'école" : "Refuser"}
            </Button>
          </>
        }
      >
        {decision?.kind === "approve" ? (
          <div className="space-y-4">
            <p className="text-sm text-foreground-600">
              L'école est créée avec ses cycles, l'année scolaire en cours et ses rôles de départ. Le
              compte administrateur de {decision.request.contactName} ({decision.request.email}) reçoit
              un mot de passe provisoire.
            </p>
            <Input
              label="Code de l'école"
              required
              value={value}
              onChange={(e) => setValue(e.target.value.toUpperCase())}
              hint="Unique : 2 à 20 lettres, chiffres ou tirets."
            />
            <div>
              <p className="mb-1.5 text-xs font-medium text-foreground-700">
                Forfait accordé{" "}
                <span className="font-normal text-foreground-500">(demandé : {PLAN_LABEL[decision.request.plan]})</span>
              </p>
              <PlanPicker value={plan} onChange={setPlan} />
            </div>
            <Input
              label="Fin de l'abonnement"
              type="date"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              hint="Vide : sans échéance. Modifiable ensuite dans « Configurer »."
            />
          </div>
        ) : (
          <Textarea
            label="Motif du refus"
            required
            rows={3}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            hint="Visible par le demandeur lorsqu'il suit sa demande."
          />
        )}
      </Modal>

      <TemporaryPasswordDialog
        admin={created?.admin ?? null}
        schoolName={created?.schoolName ?? ""}
        onClose={() => setCreated(null)}
      />
    </Card>
  );
}